import React, { useEffect, useRef, useState } from "react";
import RoundSystem from "./generator";

function App() {
  const [fading, setFading] = useState(false);
  const [showMenu, setShowMenu] = useState(true);
  const [started, setStarted] = useState(false);
  const [leftCount, setLeft] = useState(0);
  const [rightCount, setRight] = useState(0);
  const [roundEnded, setRoundEnded] = useState(false);
  const [roundActive, setRoundActive] = useState(false);
  const [activeBoxes, setActiveBoxes] = useState([]);
  const [finalScores, setFinalScores] = useState({ left: 0, right: 0 });
  const [audioLoaded, setAudioLoaded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const clickSoundPool = useRef([]);
  const decrementSoundPool = useRef([]);
  const currentClickIndex = useRef(0);
  const currentDecrementIndex = useRef(0);
  const startSound = useRef(null);
  const winSound = useRef(null);
  const audioUnlocked = useRef(false);

  const unlockAudio = () => {
    if (audioUnlocked.current) return;

    const silent = new Audio();
    silent.src =
      "data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA";
    silent.play().catch(() => {});
    audioUnlocked.current = true;
  };

  useEffect(() => {
    const loadAudio = async () => {
      clickSoundPool.current = await Promise.all(
        Array.from({ length: 15 }, async () => {
          const audio = new Audio("/Audio/ButtonClick.ogg");
          audio.preload = "auto";
          audio.volume = 0.5;
          return audio;
        })
      );

      decrementSoundPool.current = await Promise.all(
        Array.from({ length: 15 }, async () => {
          const audio = new Audio("/Audio/D.ogg");
          audio.preload = "auto";
          audio.volume = 0.3;
          return audio;
        })
      );

      startSound.current = new Audio("/Audio/Game.ogg");
      startSound.current.preload = "auto";
      startSound.current.volume = 0.7;

      winSound.current = new Audio("/Audio/win.ogg");
      winSound.current.preload = "auto";
      winSound.current.volume = 0.8;

      setAudioLoaded(true);
    };

    loadAudio();
  }, []);

  useEffect(() => {
    if (roundEnded && winSound.current && audioLoaded) {
      const clone = winSound.current.cloneNode();
      clone.volume = 0.8;
      clone.play().catch(() => {});
    }
  }, [roundEnded, audioLoaded]);

  useEffect(() => {
    const checkMobile = () => {
      const hasTouch =
        "ontouchstart" in window || navigator.maxTouchPoints > 0;
      const isSmallScreen = window.innerWidth <= 768;
      const isMobileDevice =
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent
        );

      setIsMobile((hasTouch && isSmallScreen) || isMobileDevice);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const playClickSound = () => {
    if (!audioLoaded || clickSoundPool.current.length === 0) return;

    const audio = clickSoundPool.current[currentClickIndex.current];
    const clone = audio.cloneNode();
    clone.volume = 0.5;
    clone.play().catch(() => {});
    currentClickIndex.current =
      (currentClickIndex.current + 1) % clickSoundPool.current.length;
  };

  const handleStartClick = () => {
    unlockAudio();

    if (startSound.current && audioLoaded) {
      const clone = startSound.current.cloneNode();
      clone.volume = 0.7;
      clone.play().catch(() => {});
    }

    setFading(true);
    setTimeout(() => {
      setShowMenu(false);
      setFading(false);
    }, 500);
  };

  const handleLeftIncrement = (event) => {
    event.preventDefault();
    unlockAudio();
    if (!roundActive) return;

    playClickSound();
    setLeft((prev) => prev + 1);
  };

  const handleRightIncrement = (event) => {
    event.preventDefault();
    unlockAudio();
    if (!roundActive) return;

    playClickSound();
    setRight((prev) => prev + 1);
  };

  useEffect(() => {
    if (isMobile) return;

    const handleKeyDown = (event) => {
      if (!roundActive) return;

      const playKeyboardClick = () => {
        if (!audioLoaded || clickSoundPool.current.length === 0) return;

        const audio = clickSoundPool.current[currentClickIndex.current];
        const clone = audio.cloneNode();
        clone.volume = 0.5;
        clone.play().catch(() => {});
        currentClickIndex.current =
          (currentClickIndex.current + 1) % clickSoundPool.current.length;
      };

      const playKeyboardDecrement = () => {
        if (!audioLoaded || decrementSoundPool.current.length === 0) return;

        const audio = decrementSoundPool.current[currentDecrementIndex.current];
        const clone = audio.cloneNode();
        clone.volume = 0.3;
        clone.play().catch(() => {});
        currentDecrementIndex.current =
          (currentDecrementIndex.current + 1) %
          decrementSoundPool.current.length;
      };

      if (event.key === "a" || event.key === "A") {
        event.preventDefault();
        playKeyboardClick();
        setLeft((prev) => prev + 1);
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        playKeyboardClick();
        setRight((prev) => prev + 1);
      } else if (event.key === "s" || event.key === "S") {
        event.preventDefault();
        setLeft((prev) => {
          if (prev > 0) {
            playKeyboardDecrement();
            return prev - 1;
          }

          return prev;
        });
      } else if (event.key === "ArrowLeft") {
        event.preventDefault();
        setRight((prev) => {
          if (prev > 0) {
            playKeyboardDecrement();
            return prev - 1;
          }

          return prev;
        });
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [audioLoaded, isMobile, roundActive]);

  const getWinner = () => {
    if (finalScores.left > finalScores.right) {
      return { winner: "Left Player Wins!", color: "#3498db", emoji: "🏆" };
    }

    if (finalScores.right > finalScores.left) {
      return { winner: "Right Player Wins!", color: "#e74c3c", emoji: "🏆" };
    }

    return { winner: "It's a Draw!", color: "#9b59b6", emoji: "🤝" };
  };

  const cells = Array.from({ length: 100 }, (_, index) => {
    const boxInfo = activeBoxes.find((box) => box.index === index);
    const cellClass = boxInfo ? boxInfo.side : "";

    return (
      <div
        key={index}
        style={{
          background:
            cellClass === "left"
              ? "#3498db"
              : cellClass === "right"
                ? "#e74c3c"
                : "rgba(255,255,255,0.05)",
          border: "1px solid rgba(255,255,255,0.1)",
          borderRadius: "8px",
          transition: "all 0.3s ease",
          boxShadow: cellClass
            ? `0 0 10px ${cellClass === "left" ? "#3498db" : "#e74c3c"}`
            : "none",
        }}
      />
    );
  });

  if (showMenu) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          background:
            "linear-gradient(135deg, #0f2027 0%, #203a43 50%, #2c5364 100%)",
          color: "white",
          opacity: fading ? 0 : 1,
          transition: "opacity 0.5s ease",
          fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
        }}
      >
        <h1
          style={{
            fontSize: "clamp(3rem, 6vw, 5rem)",
            marginBottom: "10px",
            textShadow: "0 4px 15px rgba(0,0,0,0.5)",
          }}
        >
          Box Game
        </h1>
        <p
          style={{
            fontSize: "clamp(1.2rem, 2vw, 1.5rem)",
            marginBottom: "40px",
            color: "#ccc",
          }}
        >
          Count faster than your opponent!
        </p>
        <button
          onPointerDown={handleStartClick}
          style={{
            padding: "15px 50px",
            fontSize: "1.8rem",
            background: "linear-gradient(135deg, #00b09b 0%, #96c93d 100%)",
            color: "white",
            border: "none",
            borderRadius: "30px",
            cursor: "pointer",
            fontWeight: "bold",
            boxShadow: "0 10px 20px rgba(0,0,0,0.3)",
            transition: "transform 0.2s ease, box-shadow 0.2s ease",
          }}
          onMouseEnter={(event) => {
            event.target.style.transform = "translateY(-3px)";
            event.target.style.boxShadow = "0 15px 25px rgba(0,0,0,0.4)";
          }}
          onMouseLeave={(event) => {
            event.target.style.transform = "translateY(0)";
            event.target.style.boxShadow = "0 10px 20px rgba(0,0,0,0.3)";
          }}
        >
          START
        </button>
        <p style={{ marginTop: "30px", fontSize: "1rem", color: "#888" }}>
          Mode: {isMobile ? "📱 Touch" : "🖥️ Keyboard"}
        </p>
      </div>
    );
  } // menu end

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #141e30 0%, #243b55 100%)",
        padding: "20px",
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      }}
    >
      <div
        style={{
          maxWidth: "1200px",
          margin: "0 auto",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {started && !roundEnded && (
          <RoundSystem
            started={started}
            onRoundEnd={() => {
              setRoundActive(false);
              setRoundEnded(true);
            }}
            onRoundStateChange={setRoundActive}
            leftCount={leftCount}
            rightCount={rightCount}
            onResetCounts={() => {
              setLeft(0);
              setRight(0);
            }}
            onBoxesGenerated={setActiveBoxes}
            onScoreUpdate={setFinalScores}
          />
        )}

        {!started && !roundEnded && (
          <div
            style={{
              textAlign: "center",
              color: "white",
              marginTop: "20px",
              background: "rgba(0,0,0,0.3)",
              padding: "20px",
              borderRadius: "10px",
            }}
          >
            <h2
              style={{ fontSize: "clamp(1.2rem, 2.5vw, 1.8rem)", margin: 0 }}
            >
              {isMobile
                ? "Tap the buttons to count!"
                : 'Player 1: Press "A" | Player 2: Press "→"'}
            </h2>
            {!isMobile && (
              <p
                style={{ fontSize: "1rem", color: "#aaa", marginTop: "10px" }}
              >
                Need to fix a mistake? Press "S" or "←" to decrement.
              </p>
            )}
          </div>
        )}

        {roundEnded ? (
          <div
            style={{
              textAlign: "center",
              color: "white",
              marginTop: "40px",
              background: "rgba(255,255,255,0.05)",
              padding: "50px",
              borderRadius: "20px",
              backdropFilter: "blur(10px)",
              boxShadow: "0 10px 30px rgba(0,0,0,0.5)",
            }}
          >
            <h1
              style={{
                fontSize: "clamp(2.5rem, 5vw, 4rem)",
                margin: "0 0 20px 0",
              }}
            >
              {getWinner().emoji}
            </h1>
            <h2
              style={{
                fontSize: "clamp(2rem, 4vw, 3rem)",
                color: getWinner().color,
                margin: 0,
              }}
            >
              {getWinner().winner}
            </h2>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: "40px",
                fontSize: "1.5rem",
                margin: "40px 0",
                flexWrap: "wrap",
              }}
            >
              <div
                style={{
                  background: "rgba(52, 152, 219, 0.2)",
                  padding: "20px",
                  borderRadius: "15px",
                  border: "2px solid #3498db",
                }}
              >
                <p
                  style={{ margin: 0, color: "#3498db", fontWeight: "bold" }}
                >
                  Left Player
                </p>
                <p style={{ margin: "10px 0 0 0", fontSize: "2.5rem" }}>
                  {finalScores.left}
                </p>
              </div>
              <div
                style={{
                  background: "rgba(231, 76, 60, 0.2)",
                  padding: "20px",
                  borderRadius: "15px",
                  border: "2px solid #e74c3c",
                }}
              >
                <p
                  style={{ margin: 0, color: "#e74c3c", fontWeight: "bold" }}
                >
                  Right Player
                </p>
                <p style={{ margin: "10px 0 0 0", fontSize: "2.5rem" }}>
                  {finalScores.right}
                </p>
              </div>
            </div>
            <button
              onPointerDown={() => window.location.reload()}
              style={{
                padding: "15px 40px",
                fontSize: "1.5rem",
                background: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
                color: "white",
                border: "none",
                borderRadius: "30px",
                cursor: "pointer",
                fontWeight: "bold",
                transition: "transform 0.2s ease",
              }}
              onMouseEnter={(event) => {
                event.target.style.transform = "scale(1.05)";
              }}
              onMouseLeave={(event) => {
                event.target.style.transform = "scale(1)";
              }}
            >
              Play Again
            </button>
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              alignItems: "center",
              justifyContent: "center",
              gap: "40px",
              marginTop: "30px",
              width: "100%",
            }}
          >
            {!isMobile && (
              <div style={{ textAlign: "center", color: "white", flex: 1 }}>
                <p
                  style={{
                    fontSize: "4rem",
                    fontWeight: "bold",
                    margin: "0 0 10px 0",
                    color: "#3498db",
                  }}
                >
                  {leftCount}
                </p>
                <p style={{ margin: "0 0 12px 0", color: "#9ed8ff" }}>
                  Score: {finalScores.left}
                </p>
                <div
                  style={{
                    width: "120px",
                    height: "120px",
                    background: roundActive
                      ? "linear-gradient(135deg, #2980b9 0%, #3498db 100%)"
                      : "#555",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "20px",
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "20px",
                    margin: "0 auto",
                    boxShadow: roundActive
                      ? "0 10px 20px rgba(52, 152, 219, 0.4)"
                      : "none",
                    transition: "all 0.3s ease",
                  }}
                >
                  LEFT
                </div>
              </div>
            )}

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(10, 1fr)",
                gap: "6px",
                width: isMobile ? "90vw" : "500px",
                maxWidth: "500px",
                aspectRatio: "1/1",
                background: "rgba(0,0,0,0.2)",
                padding: "10px",
                borderRadius: "15px",
                boxShadow: "inset 0 4px 10px rgba(0,0,0,0.5)",
              }}
            >
              {cells}
            </div>

            {!isMobile && (
              <div style={{ textAlign: "center", color: "white", flex: 1 }}>
                <p
                  style={{
                    fontSize: "4rem",
                    fontWeight: "bold",
                    margin: "0 0 10px 0",
                    color: "#e74c3c",
                  }}
                >
                  {rightCount}
                </p>
                <p style={{ margin: "0 0 12px 0", color: "#ffb0a9" }}>
                  Score: {finalScores.right}
                </p>
                <div
                  style={{
                    width: "120px",
                    height: "120px",
                    background: roundActive
                      ? "linear-gradient(135deg, #c0392b 0%, #e74c3c 100%)"
                      : "#555",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "20px",
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "20px",
                    margin: "0 auto",
                    boxShadow: roundActive
                      ? "0 10px 20px rgba(231, 76, 60, 0.4)"
                      : "none",
                    transition: "all 0.3s ease",
                  }}
                >
                  RIGHT
                </div>
              </div>
            )}

            {isMobile && (
              <div
                style={{
                  display: "flex",
                  gap: "20px",
                  width: "100%",
                  justifyContent: "center",
                  marginTop: "10px",
                }}
              >
                <button
                  onPointerDown={handleLeftIncrement}
                  disabled={!roundActive}
                  style={{
                    flex: 1,
                    padding: "20px",
                    fontSize: "1.2rem",
                    fontWeight: "bold",
                    background: roundActive
                      ? "linear-gradient(135deg, #2980b9 0%, #3498db 100%)"
                      : "#555",
                    color: "white",
                    border: "none",
                    borderRadius: "15px",
                    boxShadow: roundActive
                      ? "0 8px 15px rgba(52, 152, 219, 0.4)"
                      : "none",
                    opacity: roundActive ? 1 : 0.5,
                  }}
                >
                  LEFT ({leftCount}) | Score {finalScores.left}
                </button>
                <button
                  onPointerDown={handleRightIncrement}
                  disabled={!roundActive}
                  style={{
                    flex: 1,
                    padding: "20px",
                    fontSize: "1.2rem",
                    fontWeight: "bold",
                    background: roundActive
                      ? "linear-gradient(135deg, #c0392b 0%, #e74c3c 100%)"
                      : "#555",
                    color: "white",
                    border: "none",
                    borderRadius: "15px",
                    boxShadow: roundActive
                      ? "0 8px 15px rgba(231, 76, 60, 0.4)"
                      : "none",
                    opacity: roundActive ? 1 : 0.5,
                  }}
                >
                  RIGHT ({rightCount}) | Score {finalScores.right}
                </button>
              </div>
            )}
          </div>
        )}

        <div style={{ textAlign: "center", marginTop: "40px" }}>
          {!started && (
            <button
              onPointerDown={() => setStarted(true)}
              style={{
                padding: "15px 50px",
                fontSize: "1.8rem",
                background: "linear-gradient(135deg, #f39c12 0%, #f1c40f 100%)",
                color: "white",
                border: "none",
                borderRadius: "30px",
                cursor: "pointer",
                fontWeight: "bold",
                boxShadow: "0 10px 20px rgba(243, 156, 18, 0.3)",
              }}
            >
              Start Game!
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default App;
